// Shared formatting for meter `read` values (lifetime cumulative gallons and
// the physical LCD read). Used by both the long-term reads chart and the
// in-sync reads chart embedded in the Meter Events section.

/**
 * Compact magnitude for axis ticks, e.g. 99999871 → "100M", 638444 → "638.4K".
 * Keeps tick labels narrow so large lifetime/LCD values don't overflow the
 * chart border. Full precision is shown in tooltips and stat boxes.
 */
export function formatCompact(value: number | null | undefined): string {
  if (value == null) return '';
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Exact [min, max] of the finite values, expanded by `pad` fraction of the
 * range on each side. Returns undefined when there are no finite values.
 *
 * Used for the reads chart Y axes: applying the *same* fractional padding to
 * the lifetime-read and LCD-read axes (rather than each axis auto-scaling
 * independently) means that when the two series differ only by a unit
 * conversion — i.e. they are linearly related — every point maps to the same
 * vertical position and the lines overlap exactly. A genuine divergence
 * (e.g. an LCD rollover) still renders as a separation.
 */
export function paddedDomain(
  values: (number | null | undefined)[],
  pad = 0.05,
): [number, number] | undefined {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity) return undefined;
  if (min === max) {
    // Flat series: give it a small symmetric window so it renders mid-plot.
    const eps = Math.abs(min) > 0 ? Math.abs(min) * 0.01 : 1;
    return [min - eps, max + eps];
  }
  const p = (max - min) * pad;
  return [min - p, max + p];
}

/** Human label for the LCD read unit code stored in the DB. */
export function unitLabel(unit: string | null | undefined): string {
  switch (unit) {
    case 'G':
      return 'gal';
    case 'F':
      return 'ft³';
    case 'M':
      return 'm³';
    default:
      return unit ?? '';
  }
}

/**
 * Gallon value with thousands separators, e.g. "1,234.56 gal". Uses the same
 * 2-decimal precision as `formatLcdRead` so a gallons value and the equivalent
 * LCD value never display a different number of decimals.
 */
export function formatGallons(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} gal`;
}

/** LCD read shown with its unit, e.g. "99,999,867.71 gal". */
export function formatLcdRead(value: number | null | undefined, unit: string): string {
  if (value == null) return '—';
  const num = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return unit ? `${num} ${unit}` : num;
}

/** Signed gallon delta, e.g. "+1,234.56 gal" / "−56.00 gal". */
export function formatSignedGallons(value: number | null | undefined): string {
  return formatSignedLcd(value, 'gal');
}

/** Signed LCD delta with unit, e.g. "+12.34 gal" / "−10,000,000 gal". */
export function formatSignedLcd(value: number | null | undefined, unit: string): string {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const num = Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return unit ? `${sign}${num} ${unit}` : `${sign}${num}`;
}
